import { Box, Button, CardMedia, CircularProgress, Grid, Theme, Typography, useTheme } from '@mui/material';
import { Formik } from 'formik';
import React, { useState } from 'react'
import * as yup from 'yup';
import { accountNumberRegex, ifscRegex } from '../../utils/regex';
import { Bank } from '../../redux-store/types/api-types';
import { applicantStatusMasters, APPLICATION_TYPE, BankAccountTypeMaster, IS_PENNYDROP_APPLICABLE_FOR_AMC, NRIBankAccountTypeMaster, USER_ROLES } from '../../utils/constant';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import { MFSubmitButton, MFTextField } from '../../lib/formik';
import UseRadioGroup from '../../lib/formik/Radio';
import MFCheckbox from '../../lib/formik/Checkbox';
import { DownloadIcon, InfoIconFilled } from '../../assets/SvgIcons';
import { BankListObj, bseGenerateOtp, bseGenOtpReq, bseGenOtpRes, InvestorList } from '../../redux-store/actions/bank';
import { BankTextField } from '../investors/bankDetails';
import MFSelectField from '../../lib/formik/SelectField';
import DocumentUpload from '../../lib/formik/DocumentUpload';
import { Notes } from '../investors/components';
import { formatFileSize, isRMLikeRole } from '../../utils/utilityFunctions';
import { useHistory } from 'react-router';
import { pennyDropVerification } from '../../redux-store/actions/application';
import { SearchableSelect } from '../../lib/formik/searchSelectField';
import { FileUpload } from '../investors/documentDetails';
import { showError } from '../../redux-store/actions/auth';
import { useSnackbar } from 'notistack';

const documentTypeList = [
    {
        documentTypeLabel: "Cancelled Cheque",
        documentTypeValue: "Cancelled Cheque",
    }
]
export default function AddBankForm({
    editBank,
    isComingFromEdit = false,
    banksList = [],
    selectedInvestor
}: {
    editBank?: BankListObj
    isComingFromEdit?: boolean,
    banksList?: BankListObj[],
    selectedInvestor?: InvestorList
}): JSX.Element {
    const history = useHistory();
    const dispatch = useDispatch();
    const { role = "" } = useSelector((state: RootStateType) => state.auth);
    const { applicationId: investorApplicationId = "" } = useSelector((state: RootStateType) => state.id);
    const location = history?.location?.state as { banksList: BankListObj[], selectedInvObj: InvestorList };
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
    const theme = useTheme();
    const [pennyDropLoading, setPennyDropLoading] = useState<boolean>(false);
    const [documentUpload, setDocumentUpload] = useState(false);
    const [otpLoading, setOtpLoading] = useState<boolean>(false);
    const [pennyDropRes, setPennyDropRes] = useState<any>({})
    const { enqueueSnackbar } = useSnackbar();
    const { applicantId = "", clientInterfaceCode = "", applicationType = "", investorName = "", status = "", onboardingType = "" } = useSelector((state: RootStateType) => state.id);


    const totalBanks = banksList.length ? banksList : location?.banksList
    const investorObj = selectedInvestor ?? location?.selectedInvObj


    const isNRENRO = role === USER_ROLES.INVESTOR ? ['nre', 'nro'].includes((status ?? '')?.toLowerCase()) : (applicantStatusMasters[investorObj?.status ?? ""] === applicantStatusMasters.NRE) ||
        (applicantStatusMasters[investorObj?.status ?? ""] === applicantStatusMasters.NRO)


    const initialValues = {
        clientCode: role === USER_ROLES.INVESTOR ? clientInterfaceCode : investorObj?.clientCode,
        applicationId: (isRMLikeRole(role)) ? investorObj?.applicationId : investorApplicationId,
        applicationType: role === USER_ROLES.INVESTOR ? applicationType : investorObj?.applicationType,
        investorType: investorObj?.investorType ?? '',
        status: role === USER_ROLES.INVESTOR ? status : investorObj?.status ?? '',
        onboardingType: role === USER_ROLES.INVESTOR ? onboardingType : investorObj?.onboardingType ?? '',
        ifscCode: editBank?.ifscCode || '',
        bankAccountNumber: editBank?.bankAccountNumber || '',
        bankAccountType: editBank?.bankAccountType || (isNRENRO ? role === USER_ROLES.INVESTOR ? status : investorObj?.status : ""),
        bankName: editBank?.bankName || '',
        bankBranch: editBank?.bankBranch || '',
        defaultBankAccount: editBank?.defaultBankAccount || false,
        isActive: editBank?.isActive || true,
        pennydropCheck: editBank?.pennydropCheck || false,
        pennydropVerified: editBank?.pennydropVerified || false,
        address1: editBank?.address1 || '',
        address2: editBank?.address2 || '',
        address3: editBank?.address3 || '',
        micrCode: editBank?.micrCode || '',
        bankAddress: editBank?.address1
            ? `${editBank?.address1}${editBank?.address2 ? ',' + editBank?.address2 : ''}${editBank?.address3 ? ',' + editBank?.address3 : ''}`
            : '',

        // bankAddress: editBank?.address2 ? `${editBank?.address1}, ${editBank?.address2}, ${editBank?.address3}` : '',
        documents: editBank?.documents?.length
            ?
            editBank?.documents?.map((doc: any) => ({
                ...doc,
                documentId: doc.documentId || '',
                documentType: doc.documentType || '',
            })) : [{
                documentType: '',
                documentId: '',
                isActive: false,
                file: undefined,
                loading: false,
                required: '',
                uniqueKey: '',
                documentName: "Cancelled cheque leaf verified",
                options: [
                    "Cancelled cheque leaf verified"
                ],
                expiryDate: null,
                applicantId: role === USER_ROLES.INVESTOR ? applicantId : investorObj?.applicantId
            }],
    };

    const validationSchema = yup.object().shape({
        ifscCode: yup
            .string()
            .nullable()
            .required('IFSC Code is required')
            .test('check-ifsc-code', (value, context) => {
                const { path, createError } = context;
                if (currencyCode?.toLowerCase() === "inr") {
                    if (!ifscRegex.test(value ?? "")) {
                        return createError({
                            message: 'Invalid IFSC Code',
                            path,
                        });
                    }
                }
                return true;
            }),
        bankAccountNumber: yup
            .string()
            .nullable()
            .required('Bank Account Number is required')
            .test('check-bank-account-number', (value, context) => {
                const { path, createError } = context;

                if (currencyCode?.toLowerCase() === "inr") {
                    if (!accountNumberRegex.test(value ?? "")) {
                        return createError({
                            message: 'Please enter valid Account Number',
                            path,
                        });
                    }
                }
                return true;
            })
            .test('bankAccountNumber', 'Account number already used', (value, context) => {
                if (isComingFromEdit) {
                    return totalBanks?.filter((bank: BankListObj) => (bank.bankAccountNumber === value && bank?.id !== editBank?.id)).length === 0;
                }
                else {
                    return totalBanks?.filter((bank: BankListObj) => bank.bankAccountNumber === value).length === 0;
                }
            }),
        bankAccountType: yup
            .string()
            .nullable()
            .test('bankAccountType', 'Bank Account Type is required', (value, context) => {
                const {
                    options: { context: optionsContext },
                    createError,
                    path,
                    parent,
                } = context;
                const fieldValue = value === null ? '' : value;
                const { applicationType, status, banks } = optionsContext || {};
                if (
                    applicationType === APPLICATION_TYPE.NON_INDIVIDUAL &&
                    !['huf'].includes(fieldValue ?? "") &&
                    !fieldValue
                ) {
                    return true;
                }
                if (
                    applicationType === APPLICATION_TYPE.NON_INDIVIDUAL &&
                    fieldValue &&
                    (![BankAccountTypeMaster.Current, BankAccountTypeMaster.Savings].includes(fieldValue))
                ) {
                    return false;
                }
                // else if (
                //     fieldValue &&
                //     // applicantStatusMasters[status] !== applicantStatusMasters.NRI &&
                //     applicantStatusMasters[status] !== applicantStatusMasters.NRE &&
                //     applicantStatusMasters[status] !== applicantStatusMasters.NRO &&
                //     (!Object.values(BankAccountTypeMaster).includes(fieldValue))
                // ) {
                //     return false;
                // }
                // if (
                //     fieldValue &&
                //     // applicantStatusMasters[status] === applicantStatusMasters.NRI &&
                //     (
                //         applicantStatusMasters[status] === applicantStatusMasters.NRE ||
                //         applicantStatusMasters[status] === applicantStatusMasters.NRO
                //     ) &&
                //     (!Object.values(NRIBankAccountTypeMaster).includes(fieldValue))
                // ) {
                //     return false;
                // }
                // if (
                //     fieldValue &&
                //     // applicantStatusMasters[status] === applicantStatusMasters.NRI &&
                //     (
                //         applicantStatusMasters[status] === applicantStatusMasters.NRE ||
                //         applicantStatusMasters[status] === applicantStatusMasters.NRO
                //     ) &&
                //     (banks?.filter(
                //         (bank: Bank) =>
                //             Object.values(NRIBankAccountTypeMaster).includes(bank.bankAccountType) &&
                //             Object.values(NRIBankAccountTypeMaster).includes(fieldValue) &&
                //             bank.bankAccountType != fieldValue
                //     ).length === 1)
                // ) {
                //     return createError({
                //         message: 'Both Bank Account Type should be same',
                //         path,
                //     });
                // }

                return !!fieldValue;
            }),
        bankName: yup.string().nullable().required('Bank Name is required'),
        bankBranch: yup.string().nullable().required('Bank Branch is required'),
        documents: yup.array().of(
            yup.object().shape({
                documentId: yup
                    .string()
                    .nullable()
                    .test('documentId', 'Document Upload is required', function (value, context) {
                        const fieldValue = value || "";
                        const { options, createError, path } = context;
                        const { applicationType = "", pennydropVerified = false, status = "", onboardingType = "" } = options?.context || {};
                        if (applicationType === APPLICATION_TYPE.INDIVIDUAL && pennydropVerified) {
                            if (!fieldValue && (applicantStatusMasters[status] === applicantStatusMasters.NRE || applicantStatusMasters[status] === applicantStatusMasters.NRO)) {
                                return createError({
                                    message: 'Document Upload is required',
                                    path,
                                });
                            }
                            else if (!fieldValue && (onboardingType?.toLowerCase() === "minor")) {
                                return createError({
                                    message: 'Document Upload is required',
                                    path,
                                })
                            }
                            else
                                return true;
                        }

                        if (!value) {
                            return createError({
                                message: 'Document Upload is required',
                                path,
                            });
                        }

                        return true;
                    }),

                documentType: yup
                    .string()
                    .nullable()
                    .test('documentType', 'Document Type is required', function (value, context) {
                        const fieldValue = value || "";
                        const { options, createError, path } = context;
                        const { applicationType = "", pennydropVerified = false, status = "", onboardingType = "" } = options?.context || {};

                        if (applicationType === APPLICATION_TYPE.INDIVIDUAL && pennydropVerified) {
                            if (!fieldValue && (applicantStatusMasters[status] === applicantStatusMasters.NRE || applicantStatusMasters[status] === applicantStatusMasters.NRO)) {
                                return createError({
                                    message: 'Document Type is required',
                                    path,
                                });
                            }
                            else if (!fieldValue && (onboardingType?.toLowerCase() === "minor")) {
                                return createError({
                                    message: 'Document Type is required',
                                    path,
                                })
                            }
                            else
                                return true;
                        }

                        if (!value) {
                            return createError({
                                message: 'Document Type is required',
                                path,
                            });
                        }

                        return true;
                    }),
            })
        )

    })

    const handleUpload = () => {
        setDocumentUpload(true);
    };
    const handleUploaded = () => {
        setDocumentUpload(false);
    };


    const handleSubmit = async (values: any) => {
        let genOtpPayload: bseGenOtpReq = {
            banksList: [],
            clientCode: '',
            document: {}
        };
        const isPennyDropVerified = values?.pennydropCheck ?? false;
        if (!isPennyDropVerified) {
            dispatch(showError('Please make sure bank is verified'));
            return;
        }
        if (isComingFromEdit) {
            const updateBanks = totalBanks?.map((bank: BankListObj) => {
                if (bank?.id === editBank?.id) {
                    return {
                        ...bank,
                        ...pennyDropRes,
                        defaultBankAccount: values?.defaultBankAccount,
                        isModified: "1"
                    }
                }
                // If the edited bank is being marked as default, unset default for previous default bank and changed isModified to "1" for the previous default bank obj as previous default bank is also getting modifed

                if (values?.defaultBankAccount && (bank?.id !== editBank?.id) && bank?.defaultBankAccount) {
                    return {
                        ...bank,
                        defaultBankAccount: false,
                        isModified: "1"
                    };
                }

                return bank
            })
            if (!values?.defaultBankAccount) {
                const checkDefaultBankAvailable = totalBanks?.filter((bank: BankListObj) => bank?.id !== editBank?.id)?.some((bank: BankListObj) => bank?.defaultBankAccount);
                if (!checkDefaultBankAvailable) {
                    dispatch(showError('Please make sure one default bank is there'));
                    return;
                }

            }
            // const checkDefaultBankAvailable = updateBanks?.some((bank: BankListObj) => bank?.defaultBankAccount);

            genOtpPayload = {
                banksList: updateBanks,
                clientCode: values?.clientCode,
                document: values?.documents[0]
            }
        }
        else {
            let updatedBanksList = [...totalBanks];
            if (values?.defaultBankAccount) {
                // If new bank is marked as default, set default to false for all existing banks and changed isModified to "1" for previous default bank
                const defaultBank = totalBanks?.find((bank: BankListObj) => bank?.defaultBankAccount);
                updatedBanksList = totalBanks?.map((bank: BankListObj) => {
                    if (bank?.id === defaultBank?.id) {
                        return {
                            ...bank,
                            defaultBankAccount: false,
                            isModified: "1"
                        }
                    }
                    else {
                        return {
                            ...bank,
                            defaultBankAccount: false
                        }
                    }
                });
            }
            genOtpPayload = {
                banksList: [...updatedBanksList, {
                    ...pennyDropRes,
                    defaultBankAccount: values?.defaultBankAccount
                }],
                clientCode: values?.clientCode,
                document: values?.documents[0]
            }
        }
        try {
            setOtpLoading(true);
            const genOtpRes = (await dispatch(bseGenerateOtp(genOtpPayload))) as unknown as bseGenOtpRes;
            if (!genOtpRes?.isSuccess) return;
            if (genOtpRes?.isSuccess) {
                history.push('/bank/otp-screen', {
                    id: genOtpRes?.id ?? "",
                    cartId: genOtpRes?.cartId ?? "",
                    message: genOtpRes?.message ?? "",
                });
            }
        } catch (e) {
            console.log('Error', e);
        } finally {
            setOtpLoading(false);
        }
    }
    return (

        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            validationSchema={validationSchema}>
            {({ handleSubmit, values, setFieldValue, setValues, dirty }) => {
                const handlePennyDropVerification = async (values: any) => {
                    try {
                        if (
                            !values?.ifscCode ||
                            !values?.bankAccountNumber ||
                            !values?.bankAccountType ||
                            !values?.bankName ||
                            !values?.bankBranch
                        ) {
                            throw `Please fill all the required(*) fields in Bank Account Details`;
                        }
                        if (currencyCode?.toLowerCase() === "inr" && !ifscRegex.test(values?.ifscCode)) {
                            throw `Invalid IFSC Code in Bank Account Details`;
                        }
                        if (currencyCode?.toLowerCase() === "inr" && !accountNumberRegex.test(values?.bankAccountNumber)) {
                            throw `Please enter valid Account Number in Bank Account Details`;
                        }
                        if (isComingFromEdit) {
                            if (!(totalBanks?.filter((bank: BankListObj) => (bank.bankAccountNumber === values?.bankAccountNumber && bank?.id !== editBank?.id)).length === 0)) {
                                throw `Account Number already exists in Bank Account Details`;
                            };
                        }
                        else {
                            if (!(totalBanks?.filter((bank: BankListObj) => bank.bankAccountNumber === values?.bankAccountNumber).length === 0)) {
                                throw `Account Number already exists in Bank Account Details`;
                            }
                        }
                        setPennyDropLoading(true);
                        const pennyDropResponse = (await dispatch(
                            pennyDropVerification({
                                ...values,
                                bankAccountType:
                                    values?.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL &&
                                        !['huf'].includes(values?.investorType || '')
                                        ? BankAccountTypeMaster.Current
                                        : values?.bankAccountType,
                                postOnboarding: true,
                                investorName: role === USER_ROLES.INVESTOR ? investorName : selectedInvestor?.customerName,
                                ...(isComingFromEdit ? { isModified: "1" } : {}),
                            })
                        )) as unknown as Bank;
                        if (pennyDropResponse?.pennydropVerified) {
                            enqueueSnackbar('Verified Successfully', {
                                variant: 'success',
                                autoHideDuration: 3000,
                            });
                        }
                        else {
                            enqueueSnackbar('Verification Failed', {
                                variant: 'error',
                                autoHideDuration: 3000,
                            })
                        }
                        setPennyDropRes(pennyDropResponse ?? {});
                        if (pennyDropResponse?.pennydropCheck) {
                            setFieldValue('pennydropCheck', true);
                        }
                        if (pennyDropResponse?.pennydropVerified) {
                            setFieldValue('pennydropVerified', true);
                        } else {
                            setFieldValue('pennydropVerified', false);
                        }
                    } catch (e: any) {
                        dispatch(showError(e));
                    } finally {
                        setPennyDropLoading(false);
                    }

                }
                return (
                    <Grid
                        container
                        rowSpacing={0.5}
                        columnSpacing={3}
                        component="form"
                        onSubmit={handleSubmit}>
                        <Grid item xs={12} lg={6}>
                            <BankTextField
                                name={`ifscCode`}
                                bankFieldName={`bankName`}
                                branchFieldName={`bankBranch`}
                                disabled={false || (isComingFromEdit && editBank?.mandateFlag)}
                                pennydropFieldCheck={`pennydropCheck`}
                                address1FieldName={`address1`}
                                address2FieldName={`address2`}
                                address3FieldName={`address3`}
                                micrFieldName={`micrCode`}
                                addressFieldName={`bankAddress`}
                                currencyCode={currencyCode}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <MFTextField
                                name={`bankAccountNumber`}
                                label="Bank Account Number *"
                                placeholder="Enter Bank Account Number"
                                disabled={false || (isComingFromEdit && editBank?.mandateFlag)}
                                onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
                                    setValues({
                                        ...values,
                                        pennydropCheck: false,
                                        bankAccountNumber: value
                                    })
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <MFTextField
                                name={`micrCode`}
                                label="MICR Code"
                                placeholder="Enter MICR Code"
                                disabled={currencyCode?.toLowerCase() === "inr"}
                                value={values?.micrCode ?? ""}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6} sx={{ display: { xs: 'none', sm: 'block' } }} />
                        <Grid item xs={12} lg={4}>
                            {values.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL &&
                                !['huf'].includes(values.investorType || '') ? (
                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: 'rgba(0,0,0,0.7)',
                                        mt: { xs: 0, sm: 5 },
                                        mb: { xs: 1, sm: 0 },
                                    }}>
                                    Bank type: Current{' '}
                                </Typography>
                            ) : (
                                <UseRadioGroup
                                    formLabel="Bank type*"
                                    name={`bankAccountType`}
                                    value={values.bankAccountType}
                                    onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
                                        setFieldValue('bankAccountType', value);
                                        setFieldValue('pennydropCheck', false);
                                    }}

                                    items={Object.keys(
                                        (
                                            (applicantStatusMasters[values?.bankAccountType as string] === applicantStatusMasters.NRE) ||
                                            (applicantStatusMasters[values?.bankAccountType as string] === applicantStatusMasters.NRO)
                                        )
                                            ? NRIBankAccountTypeMaster
                                            : BankAccountTypeMaster
                                    ).map((accountType) => ({
                                        label: accountType,
                                        value:
                                            // applicantStatusMasters[values.status as string] ===
                                            //   applicantStatusMasters.NRI
                                            //   ? NRIBankAccountTypeMaster[accountType]
                                            //   : BankAccountTypeMaster[accountType],
                                            (
                                                (applicantStatusMasters[values?.bankAccountType as string] === applicantStatusMasters.NRE) ||
                                                (applicantStatusMasters[values?.bankAccountType as string] === applicantStatusMasters.NRO)
                                            )
                                                ? NRIBankAccountTypeMaster[accountType]
                                                : BankAccountTypeMaster[accountType],
                                    }))}
                                    disabled={applicantStatusMasters[values?.bankAccountType as string] === applicantStatusMasters.NRE || applicantStatusMasters[values?.bankAccountType as string] === applicantStatusMasters.NRO || (isComingFromEdit && editBank?.mandateFlag)}
                                />
                            )}
                        </Grid>
                        <Grid item lg={8} sx={{ display: { xs: 'none', lg: 'block' } }} />
                        <Grid item xs={12}>
                            <MFTextField
                                name={`bankName`}
                                label="Bank Name *"
                                placeholder="Enter Bank Name"
                                disabled={currencyCode?.toLowerCase() === "inr"}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <MFTextField
                                name={`bankBranch`}
                                label="Bank Branch *"
                                placeholder="Enter Bank Branch"
                                disabled={currencyCode?.toLowerCase() === "inr"}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <MFTextField
                                name={`bankAddress`}
                                multiline
                                rows={3}
                                label="Bank Address"
                                placeholder="Enter Bank Address"
                                disabled={currencyCode?.toLowerCase() === "inr"}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ width: 'fit-content' }}>
                                <MFCheckbox
                                    name={`defaultBankAccount`}
                                    label={<span>
                                        Default Bank Account
                                        {/* <span style={{ color: theme.palette.error[500] }}> *</span> */}
                                    </span>}
                                    sx={{ letterSpacing: '1px' }}
                                    disabled={false || (isComingFromEdit && totalBanks?.length === 1)}
                                />
                            </Box>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            display="flex"
                            justifyContent="space-between"
                            sx={{
                                width: '100%',
                                mt: 2,
                                flexDirection: { xs: 'column-reverse', sm: 'row' },
                                alignItems: { xs: 'flex-start', sm: 'center' },
                            }}>
                            <Grid
                                xs={12}
                                sm={3}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}>
                                <Button
                                    variant="outlined"
                                    type="button"
                                    sx={{
                                        color: 'primary.main',
                                        fontWeight: 600,
                                        fontSize: 14,
                                        px: 3,


                                        borderColor: 'primary.main',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        ':hover': {
                                            color: 'common.white',
                                            backgroundColor: 'primary.main',
                                        },
                                    }}
                                    onClick={() => {
                                        const bankAccountType =
                                            values.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL &&
                                                !['huf'].includes(values?.investorType || '')
                                                ? BankAccountTypeMaster.Current
                                                : values?.bankAccountType;
                                        setFieldValue('bankAccountType', bankAccountType);
                                        handlePennyDropVerification(values);
                                    }}
                                    disabled={values?.pennydropCheck}
                                >
                                    {values?.pennydropCheck && values?.pennydropVerified
                                        ? 'Verified'
                                        : 'Verify Bank Details'
                                    }
                                </Button>
                                {pennyDropLoading && (
                                    <Box sx={{ display: 'flex' }}>
                                        <CircularProgress size={30} />
                                    </Box>
                                )}
                            </Grid>
                            <Grid
                                sm={9}
                                xs={12}
                                sx={{ position: 'relative', bottom: 10, mb: { xs: 2, sm: 0 } }}>
                                <Typography
                                    component="div"
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        padding: { xs: 0, sm: '10px 20px' },
                                        borderRadius: '5px',
                                        alignItems: 'flex-start',
                                        mt: 2,
                                        width: '100%',
                                        boxSizing: 'border-box',
                                    }}>
                                    <InfoIconFilled width={24} height={24} />
                                    <Typography
                                        sx={{
                                            color: `${theme.palette.text.secondary}`,
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            ml: 1,
                                        }}>
                                        {/* {"bank.defaultBankAccount && bank.pennydropCheck && !bank.pennydropVerified".length
                                            ? 'Penny drop verification failed, Unable to verify your bank details. Please upload your default bank account proofs In Document Details'
                                            : 'The Primary Investor’s bank account will be verified using a penny drop validation'} */}
                                        {values?.pennydropCheck && !values?.pennydropVerified
                                            ? 'Penny drop verification failed, Unable to verify your bank details'
                                            : 'The Primary Investor’s bank account will be verified using a penny drop validation'}
                                    </Typography>
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid
                            item
                            sm={12}
                            xs={12}
                            sx={{ position: 'relative', bottom: { xs: 5, sm: 20 }, mb: { xs: 2, sm: 0 } }}>
                            <Typography
                                sx={{
                                    color: 'rgba(41, 49, 57, 0.7)',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    mt: 2,
                                    ml: 1,
                                }}>
                                {values?.pennydropCheck && !values?.pennydropVerified
                                    ? 'Penny drop verification failed'
                                    : null
                                }
                            </Typography>
                        </Grid>
                        <Grid container item spacing={2} mt={2} justifyContent={'space-between'}>
                            <Grid item xs={12} md={6}>
                                {!(editBank?.documents.length > 0 && editBank?.documents[0]?.file) ? <SearchableSelect
                                    label={'Bank Account Proof'}
                                    name={`documents[0].documentType`}
                                    items={[{
                                        key: "Cancelled cheque leaf verified",
                                        value: "bankAccountProof",
                                    }].map((item: { key: string; value: string }) => ({
                                        key: item.key,
                                        value: item.value,
                                    }))}
                                    // applyLabelStyles={true}
                                    disabled={false}
                                    onChange={async (event) => {
                                        setFieldValue(
                                            `documents[0].documentType`,
                                            "bankAccountProof"
                                        );
                                    }}
                                /> : (
                                    <>
                                        <Typography variant='body_500' color='text.primary'>
                                            {`Bank Account Proof`}
                                        </Typography>
                                        <Typography mt={2} variant='body_500' color='text.secondary'>
                                            {editBank?.documents?.[0]?.documentName ?? "-"}
                                        </Typography>
                                    </>
                                )}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FileUpload
                                    onFileChange={(value, documentId, removeFile) => {
                                        const { file, id, ...rest } = values?.documents[0]
                                        setFieldValue(`documents[0]`, {
                                            ...rest,
                                            documentId,
                                            ...(!removeFile && { file }),
                                        });
                                    }}
                                    name={`documents[0].documentId`}
                                    documentData={values?.documents?.[0]}
                                    disabled={false}
                                    onFileUpload={handleUpload}
                                    onFileUploaded={handleUploaded}
                                    fileUploadFormats={[
                                        "image/jpeg",
                                        "application/pdf",
                                        "image/png",
                                        "image/jpg"
                                    ]}
                                    maxUploadFiles={1}
                                    maxFileSize={'200KB'}
                                    minFileSize={'40KB'}
                                    multipleFilesCheck={'true'}
                                >
                                </FileUpload>
                            </Grid>
                        </Grid>

                        <Grid item>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: 3 }}>
                                <InfoIconFilled />
                                <Typography color='text.secondary' variant='subtitle_600'>
                                    For NRI(NRE/NRO),Minor and Non-Individuals,uploading document is mandatory
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} mt={2}>
                            <MFSubmitButton
                                disabled={otpLoading || documentUpload || !dirty}
                                loadingEnable={otpLoading}
                                label="Submit To BSE"
                                variant="contained"
                                fullWidth
                            />
                        </Grid>
                    </Grid >
                )
            }}
        </Formik >

    );
}
